import React, { Fragment, useEffect, useMemo, useState } from "react";
import Header from "./../../components/itemsManagement/Header";
import SideBar from "../../components/SideBar";
import { Row, Col, Table } from "reactstrap";
import { AiFillDelete, AiFillEdit, AiOutlineSearch } from "react-icons/ai"
import { Modal, Input } from "antd";
import Swal from "sweetalert2";

import { getAllItemApi, updateItemApi, deleteItemApi } from "../../api/items";

function formatDate(value) {
  const d = new Date(value)

  if (isNaN(d.getTime())) {
    return '—'
  }

  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatMAD(value) {
  const num = Number(value)

  if (isNaN(num)) {
    return '—'
  }

  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(num)} DH`
}

function ItemsManagement() {
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  const [editModal, setEditModal] = useState(false)
  const [editData, setEditData] = useState(null)

  useEffect(() => {
    getItems()
  }, [])

  const getItems = async () => {
    try {
      setIsLoading(true)
      setHasError(false)

      const response = await getAllItemApi()

      if (response && !Array.isArray(response)) {
        setHasError(true)
        setItems([])
        return
      }

      setItems(Array.isArray(response) ? response : [])
    }
    catch(err) {
      setHasError(true)
      setItems([])
    }
    finally {
      setIsLoading(false)
    }
  }

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return items

    return items.filter(el =>
      [el.numeroArticle, el.desination, el.responsable, el.prestataire, el.ice]
        .filter(Boolean)
        .some(field => String(field).toLowerCase().includes(term))
    )
  }, [items, searchTerm])

  const handleEditClick = (el) => {
    setEditData({
      _id: el._id,
      numeroArticle: el.numeroArticle,
      desination: el.desination,
      responsable: el.responsable,
      quantite: el.quantite,
      price: el.price,
      sumOfPrice: el.sumOfPrice,
      prestataire: el.prestataire,
      ice: el.ice,
    })
    setEditModal(true)
  }

  const handleEditChange = (e) => {
    const { name, value } = e.target
    setEditData({ ...editData, [name]: value })
  }

  const handleUpdateSubmit = async () => {
    if (isNaN(Number(editData.quantite)) || isNaN(Number(editData.price)) || isNaN(Number(editData.sumOfPrice))) {
      return Swal.fire('Valeur invalide', 'Quantité, prix unitaire et prix total doivent être des nombres.', 'error')
    }

    try {
      const { _id, ...data } = editData

      const response = await updateItemApi(_id, {
        ...data,
        quantite: Number(data.quantite),
        price: Number(data.price),
        sumOfPrice: Number(data.sumOfPrice),
      })

      if (response) {
        setEditModal(false)
        getItems()
        Swal.fire('Modifié', '', 'success')
      }
    }
    catch(err) {
      Swal.fire('Erreur', err?.response?.data?.error || '', 'error')
    }
  }

  const handleDelete = async (itemID) => {
    const confirm = await Swal.fire({
      title: 'Supprimer cet article ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#dc3545'
    })

    if (!confirm.isConfirmed) return

    try {
      await deleteItemApi(itemID)
      setItems(items.filter(el => el._id !== itemID))
    }
    catch(err) {
      Swal.fire({ title: 'Erreur!', text: "Impossible de supprimer cet article.", icon: 'error' })
    }
  }

  return (
    <Fragment>
      <section className="itemsManagementSection">
        <div className="contents">
          <Row>
            <Col md="2" className="p-0">
              <SideBar />
            </Col>
            <Col md="10" className="p-0">
              <Header />

              <div className="tableSection">
                <div className="tableToolbar">
                  <Input
                    prefix={<AiOutlineSearch />}
                    placeholder="Rechercher un article, un responsable, un prestataire…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    allowClear
                  />
                </div>

                <div className="table-wrapper">
                  {isLoading && (
                    <p className="stateMessage">Chargement des articles…</p>
                  )}

                  {!isLoading && hasError && (
                    <p className="stateMessage error">Impossible de charger les articles. Réessayez dans un instant.</p>
                  )}

                  {!isLoading && !hasError && items.length === 0 && (
                    <p className="stateMessage">Aucun article enregistré. Cliquez sur « Ajouter un article » pour commencer.</p>
                  )}

                  {!isLoading && !hasError && items.length > 0 && filteredItems.length === 0 && (
                    <p className="stateMessage">Aucun article ne correspond à « {searchTerm} ».</p>
                  )}

                  {!isLoading && !hasError && filteredItems.length > 0 && (
                    <Table striped>
                      <thead>
                        <tr>
                          <th>N° Article</th>
                          <th>Désignation</th>
                          <th>Responsable</th>
                          <th>Quantité</th>
                          <th>Prix unitaire</th>
                          <th>Prix total</th>
                          <th>Prestataire</th>
                          <th>ICE</th>
                          <th>Date</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredItems.map(el => (
                          <tr key={el._id}>
                            <td>{el.numeroArticle}</td>
                            <td>{el.desination}</td>
                            <td>{el.responsable}</td>
                            <td className="mono">{el.quantite}</td>
                            <td className="mono">{formatMAD(el.price)}</td>
                            <td className="mono">{formatMAD(el.sumOfPrice)}</td>
                            <td>{el.prestataire}</td>
                            <td className="mono">{el.ice}</td>
                            <td className="mono">{formatDate(el.createdAt)}</td>
                            <td>
                              <div className="action-icons">
                                <button className="action-btn edit-btn" onClick={() => handleEditClick(el)}><AiFillEdit size="1rem" /></button>
                                <button className="action-btn delete-btn" onClick={() => handleDelete(el._id)}><AiFillDelete size="1rem" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              </div>

            </Col>
          </Row>
        </div>
      </section>

      <Modal
        title="Modifier l'article"
        open={editModal}
        onOk={handleUpdateSubmit}
        onCancel={() => setEditModal(false)}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        {editData && (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <Input placeholder="N° Article" name="numeroArticle" value={editData.numeroArticle} onChange={handleEditChange} />
            <Input placeholder="Désignation" name="desination" value={editData.desination} onChange={handleEditChange} />
            <Input placeholder="Responsable de véhicule" name="responsable" value={editData.responsable} onChange={handleEditChange} />
            <Input placeholder="Quantité" name="quantite" value={editData.quantite} onChange={handleEditChange} />
            <Input placeholder="Prix unitaire" name="price" value={editData.price} onChange={handleEditChange} />
            <Input placeholder="Prix total de la commande" name="sumOfPrice" value={editData.sumOfPrice} onChange={handleEditChange} />
            <Input placeholder="Prestataire" name="prestataire" value={editData.prestataire} onChange={handleEditChange} />
            <Input placeholder="ICE" name="ice" value={editData.ice} onChange={handleEditChange} />
          </div>
        )}
      </Modal>
    </Fragment>
  );
}

export default ItemsManagement;
